import { useAuth } from '@stores/auth-store'
import { SERVER_URL } from '@pma/utils/api-routes'
import { ACCESS_TOKEN } from '@pma/utils/constants'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { parseCookies } from 'nookies'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/common/card'
import { DictType } from '@pma/types'
import { Button } from '@components/common/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@components/common/hover-card'
import { Separator } from '@components/common/separator'
import AddGisEndpoint from '../gis/add-gis-endpoint'
import { toast } from '@hooks/use-toast'
import { Trash } from 'lucide-react'
import PmsLoader from '@components/common/loader'
import useTranslator from '@hooks/use-translator'


const GisSettings = () => {
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()

  const userId = authConfig?.id
  const orgId = authConfig?.last_organization_id


  const accessToken = parseCookies()[ACCESS_TOKEN]

  const getGisEndpoints = async () => {
    const response = await axios.get(
      SERVER_URL + `/settings/wms/get?context_type=organization%20user&context_id=${orgId}`,

      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['gis-endpoints'],
    queryFn: getGisEndpoints,
  })

  const deleteEndPoint = async (endpoint: DictType) => {
    const response = await axios.delete(SERVER_URL + '/settings/wms/delete', {
      params: { wms_id: endpoint.id, organization_id: orgId },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response.data
  }

  const deleteEndpointMutation = useMutation({
    mutationKey: ['delete-endpoint'],
    mutationFn: (endpoint: DictType) => deleteEndPoint(endpoint),
    onSuccess: () => {
      toast({
        title: translate('Success'),
        description: translate('Endpoint has been deleted'),
        variant: 'success',
      })

      refetch()
    },
  })

  return (
    <Card className='relative overflow-hidden'>
      <CardHeader className='flex w-full flex-row justify-between items-center border-b'>
        <CardTitle>{translate('Gis Endpoints')}</CardTitle>

          <AddGisEndpoint refetch={refetch} />

      </CardHeader>
      <CardContent className='flex flex-col gap-4 p-6 text-sm'>
        {isLoading && <PmsLoader />}

        <div className='grid gap-4'>
          <div className='flex w-full flex-col flex-wrap items-center gap-3 md:justify-between'>
            {data?.map((endpoint: DictType) => (
              <div
                className='flex w-full items-center justify-between border-b pb-2'
                key={endpoint.id}
              >
                <div className='flex items-center gap-3'>
                  <div>
                    <div className='font-medium'>{endpoint.wms.name}</div>
                    <div className='w-[22ch] truncate text-ellipsis text-xs text-muted-foreground md:w-full'>
                      {endpoint.wms.url}
                    </div>
                  </div>
                </div>
                <div className='flex flex-1 items-center justify-end gap-4'>
                  <div className='text-xs text-muted-foreground'>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button variant='link' className='text-xs'>
                          {translate('Layers')}
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className='w-80'>
                        <div className='mb-1 flex justify-between text-sm'>
                          <h4 className='font-semibold'>{translate('Name')}</h4>
                          <h4>{translate('Type')}</h4>
                        </div>
                        <Separator />
                        {endpoint?.wms?.layers?.map((layer: DictType) => (
                          <Card key={layer?.name} className='mb-1 border-none shadow-none'>
                            <CardContent className='mb-1 flex justify-between space-x-4 px-0 py-3'>
                              <h4 className='text-sm font-semibold'>{layer?.name}</h4>
                              <p className='text-xs capitalize text-slate-500'>
                                {layer.geometryType}
                              </p>
                            </CardContent>
                          </Card>
                        ))}
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  <Button
                    size={'icon'}
                    variant={'ghost'}
                    disabled={deleteEndpointMutation.isPending}
                    className='text-rose-500'
                    onClick={() => deleteEndpointMutation.mutate(endpoint)}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default GisSettings
